import { useMembershipCta } from "@/hooks/useMembershipCta";
import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  HeartHandshake,
  ArrowRight,
  ShieldAlert,
  Zap,
  CloudFog,
  Users,
  HeartCrack,
  Brain,
  type LucideIcon,
} from "lucide-react";
import { track } from "@/lib/track";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

/**
 * "Who is Recovery Trauma™ for?" — a gentle recognition carousel placed under
 * the Trustpilot strip. Each slide is one lived-experience statement a visitor
 * may recognise themselves in, closing with a reassurance card and the two
 * routes onward (free explore, or the membership journey).
 */
type AudienceItem = { line: string; icon: LucideIcon; tint: string; ink: string };

const AUDIENCE: AudienceItem[] = [
  { line: "live with C-PTSD, PTSD or the effects of complex trauma", icon: ShieldAlert, tint: "--qa-sage-tint", ink: "--qa-sage-ink" },
  { line: "struggle with emotional flashbacks, triggers or feeling constantly on alert", icon: Zap, tint: "--qa-gold-tint", ink: "--qa-gold-ink" },
  { line: "experience dissociation, numbness or feeling disconnected from yourself or your surroundings", icon: CloudFog, tint: "--qa-sky-tint", ink: "--qa-sky-ink" },
  { line: "struggle with shame, guilt, self-criticism or feeling \u201Cnot good enough\u201D", icon: HeartCrack, tint: "--qa-lavender-tint", ink: "--qa-lavender-ink" },
  { line: "notice patterns like people-pleasing, difficulty setting boundaries, fear of rejection or repeating unhealthy relationships", icon: Users, tint: "--qa-plum-tint", ink: "--qa-plum-ink" },
  { line: "feel stuck in fight, flight, freeze or fawn and want to better understand and regulate your nervous system", icon: Brain, tint: "--qa-mint-tint", ink: "--qa-mint-ink" },
];


const AUTOPLAY_MS = 5500;

export function WhoIsItForCarousel() {
  const { showJoinCta } = useMembershipCta();
  const [open, setOpen] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: true,
    containScroll: "trimSnaps",
  });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || paused) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const id = setInterval(() => emblaApi.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [emblaApi, paused]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (!open) {
    return (
      <section
        aria-labelledby="who-is-it-for-button"
        className="mx-auto flex max-w-4xl justify-center"
      >
        <button
          id="who-is-it-for-button"
          type="button"
          aria-expanded={false}
          onClick={() => {
            setOpen(true);
            track("who_is_it_for_open", {}, { source: "home_who_is_it_for" });
          }}
          className="btn-gold btn-gold--compact-mobile group"
        >
          <HeartHandshake className="h-4 w-4" strokeWidth={2} />
          Who is Recovery Trauma™ for?
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
        </button>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="who-is-it-for-heading"
      aria-roledescription="carousel"
      className="mx-auto max-w-4xl lg:max-w-[1180px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="mb-2 text-center">
        <h2
          id="who-is-it-for-heading"
          className="inline-flex items-center gap-2 font-display text-base text-foreground sm:text-lg lg:font-['Outfit'] lg:text-4xl lg:font-semibold"
        >
          <HeartHandshake className="h-4 w-4 text-[var(--gold-deep)]" strokeWidth={2} />
          Who is Recovery Trauma™ for?
        </h2>
        <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-muted-foreground sm:text-sm">
          Recovery Trauma™ is for people who are trying to understand what trauma has done to
          their emotions, relationships, nervous system and sense of self.
        </p>
        <p className="mx-auto mt-1 max-w-xl text-xs font-medium leading-relaxed text-foreground/80 sm:text-sm">
          You might find Recovery Trauma™ helpful if you:
        </p>
      </div>

      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-3 sm:gap-4">
            {AUDIENCE.map((item, i) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.line}
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${AUDIENCE.length}`}
                  style={{
                    background: `linear-gradient(160deg, color-mix(in oklab, var(${item.tint}) 85%, transparent), color-mix(in oklab, var(--card) 88%, transparent))`,
                    borderColor: `color-mix(in oklab, var(${item.ink}) 28%, transparent)`,
                  }}
                   className="flex min-w-0 shrink-0 grow-0 basis-full flex-col justify-center rounded-2xl border p-5 text-start shadow-[0_8px_24px_-18px_rgba(0,0,0,0.35)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 sm:basis-1/2 lg:basis-[calc((100%-2rem)/3)] lg:p-7"
                >
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full"
                    style={{
                      background: `color-mix(in oklab, var(${item.ink}) 16%, transparent)`,
                      color: `var(${item.ink})`,
                    }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.9} />
                  </span>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/85">{item.line}</p>
                </article>
              );
            })}

          </div>
        </div>

        <div className="mt-3 flex items-center justify-center gap-3">
          <button
            type="button"
            aria-label="Previous"
            onClick={scrollPrev}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 bg-card text-[var(--gold-deep)] transition hover:border-gold hover:shadow-[var(--shadow-gold)]"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center gap-1.5">
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to item ${i + 1}`}
                aria-current={i === selected}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === selected ? "w-5 bg-[var(--gold-deep)]" : "w-1.5 bg-gold/30"
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            aria-label="Next"
            onClick={scrollNext}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 bg-card text-[var(--gold-deep)] transition hover:border-gold hover:shadow-[var(--shadow-gold)]"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col items-center gap-2">
        {showJoinCta && <Link
          to={MEMBERSHIP_JOURNEY_PATH}
          onClick={() => {
            track(
              "cta_click",
              { cta: "who_is_it_for_join", flow: "membership_journey" },
              { source: "home_who_is_it_for" },
            );
            startMembershipJourney("who_is_it_for_join", "home_who_is_it_for");
          }}
          className="btn-gold btn-gold--compact-mobile group"
        >
          Join Recovery Trauma™
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Link>}
        <button
          type="button"
          aria-expanded={true}
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground underline-offset-4 transition hover:text-foreground hover:underline"
        >
          Show less
        </button>
      </div>

    </section>
  );
}
